<template>
  <div class="pct-management-root">
    <!-- 頁面標題 -->
    <div class="page-header">
      <h1 class="page-title" data-text="PCT 合約管理">PCT 合約管理</h1>
      <p class="page-subtitle">管理 PineconeToken 合約配置與參數</p>
    </div>
    
    <div class="management-container">
      
      <!-- 董事升級數量管理 -->
      <div class="management-card">
        <div class="card-header">
          <div class="card-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="title-group">
            <h3 class="card-title">董事升級配置</h3>
            <p class="card-subtitle">設置成為董事所需的松樹數量門檻</p>
          </div>
        </div>
        <div class="card-content">
          <div class="info-display">
            <span class="info-label">當前升級所需松樹數量</span>
            <span class="info-value highlight">{{ directorUpgradeCount }}</span>
          </div>
          <div class="form-group">
            <label class="form-label">新的升級所需數量</label>
            <div class="input-group">
              <input 
                type="number" 
                v-model="newUpgradeCount" 
                class="form-input"
                placeholder="請輸入新的升級所需數量"
                min="1"
              />
              <button 
                class="btn btn-primary" 
                @click="updateDirectorUpgradeCount"
                :disabled="isUpdating"
              >
                <span v-if="isUpdating" class="btn-loading"></span>
                {{ isUpdating ? '更新中...' : '更新數量' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 地址管理組 -->
      <div class="card-group">
        <!-- Primary DAO 地址管理 -->
        <div class="management-card">
          <div class="card-header">
            <div class="card-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <polyline points="9 22 9 12 15 12 15 22" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="title-group">
              <h3 class="card-title">主 DAO 合約地址</h3>
              <p class="card-subtitle">管理主要 DAO 合約的錢包地址</p>
            </div>
          </div>
          <div class="card-content">
            <div class="address-display">
              <span class="address-label">當前地址</span>
              <span class="address-value">{{ primaryDaoAddress || '載入中...' }}</span>
            </div>
            <div class="form-group">
              <input 
                type="text" 
                v-model="newPrimaryDaoAddress" 
                class="form-input address-input"
                placeholder="0x..."
              />
              <button 
                class="btn btn-primary" 
                @click="updatePrimaryDaoAddress"
                :disabled="isUpdatingAddress"
              >
                {{ isUpdatingAddress ? '更新中...' : '更新地址' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Team Token 地址管理 -->
        <div class="management-card">
          <div class="card-header">
            <div class="card-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="title-group">
              <h3 class="card-title">團隊代幣收款地址</h3>
              <p class="card-subtitle">設置團隊代幣分配的接收錢包</p>
            </div>
          </div>
          <div class="card-content">
            <div class="address-display">
              <span class="address-label">當前地址</span>
              <span class="address-value">{{ teamTokenAddress || '載入中...' }}</span>
            </div>
            <div class="form-group">
              <input 
                type="text" 
                v-model="newTeamTokenAddress" 
                class="form-input address-input"
                placeholder="0x..."
              />
              <button 
                class="btn btn-primary" 
                @click="updateTeamTokenAddress"
                :disabled="isUpdatingTeamAddress"
              >
                {{ isUpdatingTeamAddress ? '更新中...' : '更新地址' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Director Token 地址管理 -->
        <div class="management-card">
          <div class="card-header">
            <div class="card-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <circle cx="8.5" cy="7" r="4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <polyline points="17 11 19 13 23 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="title-group">
              <h3 class="card-title">董事代幣合約地址</h3>
              <p class="card-subtitle">管理董事身份驗證的代幣合約</p>
            </div>
          </div>
          <div class="card-content">
            <div class="address-display">
              <span class="address-label">當前地址</span>
              <span class="address-value">{{ directorTokenAddress || '載入中...' }}</span>
            </div>
            <div class="form-group">
              <input 
                type="text" 
                v-model="newDirectorTokenAddress" 
                class="form-input address-input"
                placeholder="0x..."
              />
              <button 
                class="btn btn-primary" 
                @click="updateDirectorTokenAddress"
                :disabled="isUpdatingDirectorAddress"
              >
                {{ isUpdatingDirectorAddress ? '更新中...' : '更新地址' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 官方推薦人遷移 -->
      <div class="management-card">
        <div class="card-header">
          <div class="card-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15L4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="title-group">
            <h3 class="card-title">推薦關係遷移</h3>
            <p class="card-subtitle">遷移用戶的官方推薦人關係和礦機數據</p>
          </div>
        </div>
        <div class="card-content">
          <div class="form-grid">
            <div class="form-group">
              <label class="form-label">推薦人地址</label>
              <input 
                type="text" 
                v-model="referrerAddress" 
                class="form-input address-input"
                placeholder="0x..."
              />
            </div>
            <div class="form-group">
              <label class="form-label">用戶地址</label>
              <input 
                type="text" 
                v-model="userAddress" 
                class="form-input address-input"
                placeholder="0x..."
              />
            </div>
            <div class="form-group">
              <label class="form-label">礦機數量</label>
              <input 
                type="number" 
                v-model="miningCount" 
                class="form-input"
                placeholder="請輸入礦機數量"
                min="1"
              />
            </div>
          </div>
          <button 
            class="btn btn-primary full-width" 
            @click="handleMigrateOfficial"
            :disabled="isMigrating"
          >
            {{ isMigrating ? '遷移中...' : '確認遷移' }}
          </button>
        </div>
      </div>

      <!-- Boost 等級管理 -->
      <div class="management-card">
        <div class="card-header">
          <div class="card-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="title-group">
            <h3 class="card-title">挖礦加速等級配置</h3>
            <p class="card-subtitle">設置不同等級的挖礦加速倍數</p>
          </div>
        </div>
        <div class="card-content">
          <div class="info-display mb-3">
            <span class="info-label">合約 Owner 地址</span>
            <span class="address-value small">{{ proMineOwner || '查詢中...' }}</span>
          </div>
          
          <div class="boost-levels-grid">
            <div v-for="(level, idx) in boostLevels" :key="idx" class="boost-level-item">
              <label class="form-label">等級 {{ idx }}</label>
              <input 
                type="number" 
                v-model.number="boostLevelsInput[idx]" 
                class="form-input"
                min="0" 
              />
            </div>
          </div>
          
          <div class="button-group">
            <button 
              class="btn btn-secondary" 
              @click="fetchBoostLevels" 
              :disabled="isLoadingBoostLevels"
            >
              {{ isLoadingBoostLevels ? '查詢中...' : '刷新等級' }}
            </button>
            <button 
              class="btn btn-primary" 
              @click="updateBoostLevels" 
              :disabled="isUpdatingBoostLevels"
            >
              {{ isUpdatingBoostLevels ? '更新中...' : '更新等級' }}
            </button>
          </div>
        </div>
      </div>

      <!-- 授權地址管理 -->
      <div class="management-card">
        <div class="card-header">
          <div class="card-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="11" width="18" height="10" rx="2" ry="2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="title-group">
            <h3 class="card-title">合約授權地址列表</h3>
            <p class="card-subtitle">查看和管理具有特殊權限的地址</p>
          </div>
        </div>
        <div class="card-content">
          <button 
            class="btn btn-primary mb-3" 
            @click="fetchAuthorizedAddresses" 
            :disabled="isLoadingAuthorizedAddresses"
          >
            {{ isLoadingAuthorizedAddresses ? '查詢中...' : '刷新授權地址' }}
          </button>
          
          <div class="authorized-list" v-if="authorizedAddresses.length > 0">
            <div v-for="(address, index) in authorizedAddresses" :key="index" class="authorized-item">
              <div class="authorized-info">
                <div class="info-row">
                  <span class="info-label">ID</span>
                  <span class="info-value">{{ address.id }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">角色ID</span>
                  <span class="info-value">{{ address.roleId }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">地址</span>
                  <span class="address-value small">{{ address.addr }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">描述</span>
                  <span class="info-value">{{ address.description }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <p>暫無授權地址信息</p>
          </div>
        </div>
      </div>

      <!-- 稅率管理 -->
      <div class="management-card tax-rate-card">
        <div class="card-header">
          <div class="card-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <line x1="12" y1="1" x2="12" y2="23" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="title-group">
            <h3 class="card-title">交易稅率配置</h3>
            <p class="card-subtitle">設置轉賬稅率、流動性池和銷毀比例</p>
          </div>
        </div>
        <div class="card-content">
          <!-- 當前稅率顯示區 -->
          <div class="tax-rate-display">
            <div class="current-rate-header">
              <h4 style="color: #10b981; margin: 0 0 15px 0;">當前稅率設置</h4>
              <button 
                @click="fetchTaxRate()" 
                style="background: #10b981; color: #000; padding: 5px 10px; border: none; border-radius: 5px; cursor: pointer; font-size: 12px; margin-left: 10px;"
              >
                刷新稅率
              </button>
            </div>
            <div class="current-rate-info">
              <div class="current-rate-box">
                <span class="rate-label">總稅率</span>
                <span class="rate-value">{{ transferTaxRate || 0 }}‰</span>
              </div>
              <div class="current-rate-box">
                <span class="rate-label">流動性池</span>
                <span class="rate-value">{{ currentLiquidityRate || 0 }}%</span>
              </div>
              <div class="current-rate-box">
                <span class="rate-label">銷毀</span>
                <span class="rate-value">{{ currentBurnRate || 0 }}%</span>
              </div>
              <div class="current-rate-box">
                <span class="rate-label">團隊</span>
                <span class="rate-value">{{ currentTeamRate }}%</span>
              </div>
            </div>
            <div class="rate-indicator" :style="{ width: (transferTaxRate / 200 * 100) + '%' }"></div>
          </div>
          
          <!-- 稅率配置表單 -->
          <div class="tax-form-section">
            <div class="form-row">
              <div class="form-field">
                <label class="field-label">
                  <span class="label-text">總稅率</span>
                  <span class="label-hint">0-200‰</span>
                </label>
                <div class="input-wrapper">
                  <input 
                    v-model="newTaxRate" 
                    type="number" 
                    class="tax-input"
                    :class="{ 'has-value': newTaxRate !== '' }"
                    placeholder="0"
                    min="0"
                    max="200"
                  />
                  <span class="input-suffix">‰</span>
                </div>
              </div>
            </div>

            <div class="distribution-section">
              <h4 class="section-title">稅率分配</h4>
              <div class="distribution-grid">
                <div class="distribution-field">
                  <label class="field-label">
                    <span class="label-text">流動性池</span>
                    <span class="label-value">{{ liquidityPoolRate || 0 }}%</span>
                  </label>
                  <div class="slider-wrapper">
                    <input 
                      v-model="liquidityPoolRate" 
                      type="range" 
                      class="tax-slider"
                      min="0"
                      max="100"
                    />
                    <div class="slider-fill" :style="{ width: (liquidityPoolRate || 0) + '%' }"></div>
                  </div>
                </div>

                <div class="distribution-field">
                  <label class="field-label">
                    <span class="label-text">銷毀</span>
                    <span class="label-value">{{ burnRate || 0 }}%</span>
                  </label>
                  <div class="slider-wrapper">
                    <input 
                      v-model="burnRate" 
                      type="range" 
                      class="tax-slider"
                      min="0"
                      max="100"
                    />
                    <div class="slider-fill burn-fill" :style="{ width: (burnRate || 0) + '%' }"></div>
                  </div>
                </div>
              </div>

              <!-- 分配預覽 -->
              <div class="distribution-preview" v-if="newTaxRate > 0">
                <div class="preview-title">分配預覽</div>
                <div class="preview-bars">
                  <div class="preview-bar liquidity" 
                       :style="{ width: (liquidityPoolRate || 0) + '%' }"
                       v-if="liquidityPoolRate > 0">
                    <span>流動性 {{ liquidityPoolRate }}%</span>
                  </div>
                  <div class="preview-bar burn" 
                       :style="{ width: (burnRate || 0) + '%' }"
                       v-if="burnRate > 0">
                    <span>銷毀 {{ burnRate }}%</span>
                  </div>
                  <div class="preview-bar team" 
                       :style="{ width: (100 - (Number(liquidityPoolRate) || 0) - (Number(burnRate) || 0)) + '%' }"
                       v-if="100 - (Number(liquidityPoolRate) || 0) - (Number(burnRate) || 0) > 0">
                    <span>團隊 {{ 100 - (Number(liquidityPoolRate) || 0) - (Number(burnRate) || 0) }}%</span>
                  </div>
                </div>
              </div>

              <!-- 驗證訊息 -->
              <div class="validation-message" v-if="taxValidationError">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                  <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                <span>{{ taxValidationError }}</span>
              </div>
            </div>
          </div>
          
          <!-- 更新按鈕區域 -->
          <div class="update-button-section">
            <button 
              class="btn btn-primary btn-update-tax" 
              @click="handleUpdateTaxRate()"
              :disabled="isUpdatingTax || !canUpdateTax"
              :class="{ 'ready-pulse': canUpdateTax && !isUpdatingTax }"
            >
              <span v-if="isUpdatingTax" class="btn-spinner"></span>
              <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ isUpdatingTax ? '更新中...' : '確認更新稅率' }}</span>
            </button>
            <p class="button-hint">{{ !canUpdateTax ? '請填寫所有稅率資訊' : '點擊以應用新的稅率配置' }}</p>
          </div>
        </div>
      </div>
      
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue';
import { useWalletStore } from '@/stores/walletStore';
import { writeContract, readContract } from '@wagmi/core';
import { config } from '@/composables/walletConfig';
import { abi } from '@/composables/pct_abi';
import addresses from '@/composables/addresses';
import { notify } from '@/composables/swalConfig';
import { getBoostLevels, setBoostLevels, getOwner } from '@/composables/proMineService';
import { migrateOfficial, getTransferTaxRatePerMille, updateTaxRates, getLiquidityPoolRate, getBurnRate } from '@/composables/contractPctService';
import { getAuthorizedAddressesInfo } from '@/composables/contractAuthService';

const walletStore = useWalletStore();
const newUpgradeCount = ref('');
const isUpdating = ref(false);
const newPrimaryDaoAddress = ref('');
const isUpdatingAddress = ref(false);
const newTeamTokenAddress = ref('');
const isUpdatingTeamAddress = ref(false);
const newDirectorTokenAddress = ref('');
const isUpdatingDirectorAddress = ref(false);

const boostLevels = ref([0,0,0,0,0]);
const boostLevelsInput = ref([0,0,0,0,0]);
const isLoadingBoostLevels = ref(false);
const isUpdatingBoostLevels = ref(false);

// 从 store 获取当前升级数量
const directorUpgradeCount = computed(() => walletStore.directorUpgradeCount ?? 0);

// 获取 Primary DAO 地址
const primaryDaoAddress = ref('');
// 获取 Team Token 地址
const teamTokenAddress = ref('');
// 获取 Director Token 地址
const directorTokenAddress = ref('');

// 获取 Primary DAO 地址
const fetchPrimaryDaoAddress = async () => {
  try {
    const result = await readContract(config, {
      abi,
      address: addresses.pctContractAddress,
      functionName: 'primaryDaoAddress',
      args: []
    });
    primaryDaoAddress.value = result;
  } catch (error) {
    console.error('獲取 Primary DAO 地址失敗:', error);
    notify.error('獲取地址失敗：' + (error.message || '未知錯誤'));
  }
};

// 获取 Team Token 地址
const fetchTeamTokenAddress = async () => {
  try {
    const result = await readContract(config, {
      abi,
      address: addresses.pctContractAddress,
      functionName: 'teamTokenAddress',
      args: []
    });
    teamTokenAddress.value = result;
  } catch (error) {
    console.error('獲取 Team Token 地址失敗:', error);
    notify.error('獲取地址失敗：' + (error.message || '未知錯誤'));
  }
};

// 获取 Director Token 地址
const fetchDirectorTokenAddress = async () => {
  try {
    const result = await readContract(config, {
      abi,
      address: addresses.pctContractAddress,
      functionName: 'directorTokenAddress',
      args: []
    });
    directorTokenAddress.value = result;
  } catch (error) {
    console.error('獲取 Director Token 地址失敗:', error);
    notify.error('獲取地址失敗：' + (error.message || '未知錯誤'));
  }
};

// 更新 Primary DAO 地址
const updatePrimaryDaoAddress = async () => {
  if (!newPrimaryDaoAddress.value) {
    notify.error('請輸入有效的地址');
    return;
  }

  try {
    isUpdatingAddress.value = true;
    const { request } = await writeContract(config, {
      chainId: 137,
      abi,
      address: addresses.pctContractAddress,
      functionName: 'setPrimaryDaoAddress',
      args: [newPrimaryDaoAddress.value]
    });

    const result = await request;
    if (result) {
      notify.success('更新成功');
      // 刷新数据
      await fetchPrimaryDaoAddress();
      newPrimaryDaoAddress.value = '';
    }
  } catch (error) {
    console.error('更新 Primary DAO 地址失敗:', error);
    notify.error('更新失敗：' + (error.message || '未知錯誤'));
  } finally {
    isUpdatingAddress.value = false;
  }
};

// 更新 Team Token 地址
const updateTeamTokenAddress = async () => {
  if (!newTeamTokenAddress.value) {
    notify.error('請輸入有效的地址');
    return;
  }

  try {
    isUpdatingTeamAddress.value = true;
    const { request } = await writeContract(config, {
      chainId: 137,
      abi,
      address: addresses.pctContractAddress,
      functionName: 'setTeamPayoutAddress',
      args: [newTeamTokenAddress.value]
    });

    const result = await request;
    if (result) {
      notify.success('更新成功');
      // 刷新数据
      await fetchTeamTokenAddress();
      newTeamTokenAddress.value = '';
    }
  } catch (error) {
    console.error('更新 Team Token 地址失敗:', error);
    notify.error('更新失敗：' + (error.message || '未知錯誤'));
  } finally {
    isUpdatingTeamAddress.value = false;
  }
};

// 更新 Director Token 地址
const updateDirectorTokenAddress = async () => {
  if (!newDirectorTokenAddress.value) {
    notify.error('請輸入有效的地址');
    return;
  }

  try {
    isUpdatingDirectorAddress.value = true;
    const { request } = await writeContract(config, {
      chainId: 137,
      abi,
      address: addresses.pctContractAddress,
      functionName: 'setDirectorTokenAddress',
      args: [newDirectorTokenAddress.value]
    });

    const result = await request;
    if (result) {
      notify.success('更新成功');
      // 刷新数据
      await fetchDirectorTokenAddress();
      newDirectorTokenAddress.value = '';
    }
  } catch (error) {
    console.error('更新 Director Token 地址失敗:', error);
    notify.error('更新失敗：' + (error.message || '未知錯誤'));
  } finally {
    isUpdatingDirectorAddress.value = false;
  }
};

// 更新董事升级数量
const updateDirectorUpgradeCount = async () => {
  if (!newUpgradeCount.value || Number(newUpgradeCount.value) < 1) {
    notify.error('請輸入有效的數量');
    return;
  }

  try {
    isUpdating.value = true;
    const { request } = await writeContract(config, {
      chainId: 137,
      abi,
      address: addresses.pctContractAddress,
      functionName: 'setDirectorUpgradeCount',
      args: [Number(newUpgradeCount.value)]
    });

    const result = await request;
    if (result) {
      notify.success('更新成功');
      // 刷新数据
      await walletStore.fetchAllData(true);
      newUpgradeCount.value = '';
    }
  } catch (error) {
    console.error('更新董事升級數量失敗:', error);
    notify.error('更新失敗：' + (error.message || '未知錯誤'));
  } finally {
    isUpdating.value = false;
  }
};

// 查詢 boostLevels
const fetchBoostLevels = async () => {
  isLoadingBoostLevels.value = true;
  try {
    const arr = [];
    for (let i = 0; i < 5; i++) {
      const val = await getBoostLevels(i);
      arr.push(Number(val));
    }
    boostLevels.value = arr;
    boostLevelsInput.value = [...arr];
  } catch (e) {
    notify.error('查詢 boost 等級失敗：' + (e.message || '未知錯誤'));
  } finally {
    isLoadingBoostLevels.value = false;
  }
};

// 設置 boostLevels
const updateBoostLevels = async () => {
  if (boostLevelsInput.value.length !== 5 || boostLevelsInput.value.some(v => isNaN(v) || v < 0)) {
    notify.error('請輸入正確的 5 個等級數值');
    return;
  }
  isUpdatingBoostLevels.value = true;
  try {
    // 明確構造長度為5的 string 數組
    const arr = [
      String(boostLevelsInput.value[0]),
      String(boostLevelsInput.value[1]),
      String(boostLevelsInput.value[2]),
      String(boostLevelsInput.value[3]),
      String(boostLevelsInput.value[4])
    ];
    console.log('setBoostLevels 參數:', arr, '長度:', arr.length, '型別:', arr.map(x => typeof x));
    await setBoostLevels(arr);
    notify.success('Boost 等級已更新');
    await fetchBoostLevels();
  } catch (e) {
    notify.error('更新 boost 等級失敗：' + (e.message || '未知錯誤'));
  } finally {
    isUpdatingBoostLevels.value = false;
  }
};

const proMineOwner = ref('');

const fetchProMineOwner = async () => {
  try {
    proMineOwner.value = await getOwner();
  } catch (e) {
    proMineOwner.value = '查詢失敗';
  }
};

// 官方推薦人遷移相關
const referrerAddress = ref('');
const userAddress = ref('');
const miningCount = ref('');
const isMigrating = ref(false);

const handleMigrateOfficial = async () => {
  if (!referrerAddress.value || !userAddress.value || !miningCount.value) {
    notify.error('請填寫完整信息');
    return;
  }

  try {
    isMigrating.value = true;
    await migrateOfficial(referrerAddress.value, userAddress.value, miningCount.value);
    notify.success('遷移成功');
    // 清空輸入
    referrerAddress.value = '';
    userAddress.value = '';
    miningCount.value = '';
  } catch (error) {
    console.error('遷移失敗:', error);
    notify.error('遷移失敗：' + (error.message || '未知錯誤'));
  } finally {
    isMigrating.value = false;
  }
};

// 授權地址相關
const authorizedAddresses = ref([]);
const isLoadingAuthorizedAddresses = ref(false);

const fetchAuthorizedAddresses = async () => {
  try {
    isLoadingAuthorizedAddresses.value = true;
    const addresses = await getAuthorizedAddressesInfo();
    authorizedAddresses.value = addresses;
  } catch (error) {
    console.error('獲取授權地址失敗:', error);
    notify.error('獲取授權地址失敗：' + (error.message || '未知錯誤'));
  } finally {
    isLoadingAuthorizedAddresses.value = false;
  }
};

const transferTaxRate = ref(0);
const currentLiquidityRate = ref(0); // 當前流動性池比例
const currentBurnRate = ref(0); // 當前銷毀比例
const currentTeamRate = computed(() => {
  const liquidity = currentLiquidityRate.value || 0;
  const burn = currentBurnRate.value || 0;
  return Math.max(0, 100 - liquidity - burn);
});

const newTaxRate = ref(''); // 清空默認值
const liquidityPoolRate = ref(''); // 清空默認值
const burnRate = ref(''); // 清空默認值
const isUpdatingTax = ref(false);
const taxValidationError = ref('');
const canUpdateTax = computed(() => {
  const rate = Number(newTaxRate.value);
  const pool = Number(liquidityPoolRate.value) || 0;
  const burn = Number(burnRate.value) || 0;
  
  return rate >= 0 && rate <= 200 && 
         pool >= 0 && pool <= 100 && 
         burn >= 0 && burn <= 100 && 
         pool + burn <= 100 &&
         newTaxRate.value !== '';
});

// 使用 watch 來更新驗證錯誤訊息
watch([newTaxRate, liquidityPoolRate, burnRate], () => {
  const rate = Number(newTaxRate.value);
  const pool = Number(liquidityPoolRate.value) || 0;
  const burn = Number(burnRate.value) || 0;
  
  if (rate < 0 || rate > 200) {
    taxValidationError.value = rate < 0 ? '稅率不能為負數' : '稅率不能超過200‰';
  } else if (pool + burn > 100) {
    taxValidationError.value = '流動性池和銷毀比例之和不能超過100%';
  } else {
    taxValidationError.value = '';
  }
});

// 获取税率
const fetchTaxRate = async () => {
  try {
    // 獲取總稅率（千分比）
    const totalRate = await getTransferTaxRatePerMille();
    console.log('獲取到總稅率 (‰):', totalRate, '類型:', typeof totalRate);
    
    // 獲取流動性池比例（百分比）
    const liquidityRate = await getLiquidityPoolRate();
    console.log('獲取到流動性池比例 (%):', liquidityRate, '類型:', typeof liquidityRate);
    
    // 獲取銷毀比例（百分比）
    const burnRateValue = await getBurnRate();
    console.log('獲取到銷毀比例 (%):', burnRateValue, '類型:', typeof burnRateValue);
    
    // 團隊比例 = 100 - 流動性池 - 銷毀
    const teamRate = 100 - liquidityRate - burnRateValue;
    console.log('計算團隊比例 (%):', teamRate);
    
    // 使用 nextTick 確保 DOM 更新
    await nextTick();
    
    // 更新響應式變數，確保是數字類型
    transferTaxRate.value = Number(totalRate) || 0;
    currentLiquidityRate.value = Number(liquidityRate) || 0;
    currentBurnRate.value = Number(burnRateValue) || 0;
    
    // 再次使用 nextTick 確保更新
    await nextTick();
    
    console.log('更新後的響應式值:', {
      transferTaxRate: transferTaxRate.value,
      currentLiquidityRate: currentLiquidityRate.value,
      currentBurnRate: currentBurnRate.value
    });
    
  } catch (error) {
    console.error('获取税率失败:', error);
    notify.error('获取税率失败');
    // 設置默認值避免顯示問題
    transferTaxRate.value = 0;
    currentLiquidityRate.value = 0;
    currentBurnRate.value = 0;
  }
};


// 更新税率
const handleUpdateTaxRate = async () => {
  // 使用 walletStore.address 來檢查錢包是否連接
  if (!walletStore.address) {
    notify.error('請先連接錢包');
    return;
  }
  
  const totalRate = Number(newTaxRate.value) || 0;
  const poolRate = Number(liquidityPoolRate.value) || 0;
  const burnRateValue = Number(burnRate.value) || 0;

  if (totalRate < 0 || totalRate > 200) {
    notify.error('總稅率必須在0-200‰之間');
    return;
  }

  if (poolRate < 0 || poolRate > 100) {
    notify.error('流動性池比例必須在0-100%之間');
    return;
  }

  if (burnRateValue < 0 || burnRateValue > 100) {
    notify.error('銷毀比例必須在0-100%之間');
    return;
  }

  if (poolRate + burnRateValue > 100) {
    notify.error('流動性池比例和銷毀比例之和不能超過100%');
    return;
  }

  try {
    isUpdatingTax.value = true;
    console.log('準備更新稅率，參數:', {
      總稅率: totalRate + '‰',
      流動性池: poolRate + '%',
      銷毀: burnRateValue + '%',
      團隊: (100 - poolRate - burnRateValue) + '%'
    });
    
    await updateTaxRates(totalRate, poolRate, burnRateValue);
    
    notify.success('稅率更新成功');
    
    // 重新獲取最新的稅率
    await fetchTaxRate();
    
    // 清空表單
    newTaxRate.value = '';
    liquidityPoolRate.value = '';
    burnRate.value = '';
    taxValidationError.value = '';
  } catch (error) {
    console.error('更新稅率失敗 - 詳細錯誤:', error);
    console.error('Error message:', error.message);
    console.error('Error cause:', error.cause);
    console.error('Error stack:', error.stack);
    
    // 顯示更詳細的錯誤訊息
    if (error.message?.includes('user rejected') || error.message?.includes('User rejected')) {
      notify.error('用戶取消了交易');
    } else if (error.message?.includes('insufficient funds')) {
      notify.error('餘額不足');
    } else if (error.message?.includes('Only owner') || error.message?.includes('Ownable')) {
      notify.error('只有合約擁有者可以更新稅率');
    } else if (error.message?.includes('connector not found')) {
      notify.error('錢包連接器未找到，請重新連接錢包');
    } else if (error.cause?.reason) {
      notify.error('合約錯誤：' + error.cause.reason);
    } else {
      notify.error('更新稅率失敗：' + (error.message || '未知錯誤'));
    }
  } finally {
    isUpdatingTax.value = false;
  }
};

// 页面加载时获取数据
onMounted(async () => {
  await walletStore.fetchAllData(true);
  await fetchPrimaryDaoAddress();
  await fetchTeamTokenAddress();
  await fetchDirectorTokenAddress();
  await fetchBoostLevels();
  await fetchProMineOwner();
  await fetchAuthorizedAddresses();
  await fetchTaxRate();
});
</script>

<style scoped>
/* CSS 變量定義 - 嚴格遵循設計系統 */
:root {
  --primary-gold: #10b981;
  --primary-gold-light: #6ee7b7;
  --primary-gold-dark: #059669;
  --primary-gold-muted: #047857;
  
  --bg-primary: #0a1a0a;
  --bg-secondary: #0d1a0d;
  --bg-tertiary: #152015;
  --bg-elevated: #1a2e1a;
  
  --accent-teal: #6ee7b7;
  --accent-teal-dark: #44a39a;
  --accent-warm: #f4e4bc;
  
  --text-primary: #6ee7b7;
  --text-secondary: #059669;
  --text-muted: #047857;
  --text-inverse: #0a1a0a;
  
  --transition-fast: 0.2s;
  --transition-base: 0.3s;
  --transition-slow: 0.5s;
  
  --ease-out: cubic-bezier(0, 0, 0.2, 1);
  --ease-in-out: cubic-bezier(0.4, 0, 0.2, 1);
}

/* 根容器 - 增強黑金奢華感 */
.pct-management-root {
  min-height: 100vh;
  background: var(--bg-primary);
  padding: 40px 20px 80px;
  position: relative;
  overflow-x: hidden;
  font-family: 'Lato Semibold', 'Source Han Sans', sans-serif;
}

/* 背景裝飾 - 金色光暈效果 */
.pct-management-root::before {
  content: '';
  position: fixed;
  top: -50%;
  right: -25%;
  width: 80%;
  height: 80%;
  background: radial-gradient(circle, rgba(16, 185, 129, 0.05) 0%, transparent 70%);
  border-radius: 50%;
  filter: blur(60px);
  pointer-events: none;
  animation: float 20s ease-in-out infinite;
  z-index: 0;
}

.pct-management-root::after {
  content: '';
  position: fixed;
  bottom: -50%;
  left: -25%;
  width: 80%;
  height: 80%;
  background: radial-gradient(circle, rgba(16, 185, 129, 0.03) 0%, transparent 70%);
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  animation: float 25s ease-in-out infinite reverse;
  z-index: 0;
}


/* 頁面標題 - 豪華金色風格 */
.page-header {
  text-align: center;
  margin-bottom: 48px;
  animation: fadeIn 0.6s var(--ease-out);
  position: relative;
  padding: 20px;
  background: radial-gradient(ellipse at center top, rgba(16, 185, 129, 0.05) 0%, transparent 50%);
}

/* 標題上方金色光暈 */
.page-header::before {
  content: '';
  position: absolute;
  top: -50px;
  left: 50%;
  transform: translateX(-50%);
  width: 300px;
  height: 100px;
  background: radial-gradient(ellipse at center, rgba(16, 185, 129, 0.15) 0%, transparent 70%);
  filter: blur(40px);
  pointer-events: none;
  animation: pulse 4s ease-in-out infinite;
}

/* 標題裝飾線 */
.page-header::after {
  content: '';
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  width: 120px;
  height: 3px;
  background: linear-gradient(90deg, transparent, var(--primary-gold), transparent);
  border-radius: 2px;
  box-shadow: 0 0 20px rgba(16, 185, 129, 0.5);
  animation: shimmer 3s ease-in-out infinite;
}

.page-title {
  font-size: 42px;
  font-weight: 800;
  background: 
    linear-gradient(90deg, var(--primary-gold-light) 0%, var(--primary-gold) 50%, var(--primary-gold-light) 100%),
    linear-gradient(180deg, var(--primary-gold) 0%, var(--primary-gold-dark) 100%);
  background-size: 200% 100%, 100% 100%;
  background-position: 0% 0%, 0% 0%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 12px 0;
  letter-spacing: 2px;
  text-transform: uppercase;
  text-shadow: 0 0 40px rgba(16, 185, 129, 0.4);
  animation: titleShine 4s ease-in-out infinite;
  position: relative;
  display: inline-block;
}

/* 標題光暈效果 */
.page-title::after {
  content: attr(data-text);
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
  background: linear-gradient(90deg, var(--primary-gold) 0%, var(--primary-gold-light) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: blur(20px);
  opacity: 0.5;
}

.page-subtitle {
  font-size: 18px;
  color: var(--text-muted);
  margin: 0;
  opacity: 0.9;
  font-weight: 500;
  letter-spacing: 1px;
  animation: fadeIn 0.8s ease-out 0.2s both;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

/* 容器 */
.management-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: relative;
  z-index: 1;
}

/* 卡片組 */
.card-group {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
}

/* 頁面進入動畫 */
.management-card {
  animation: cardEntrance 0.8s cubic-bezier(0.23, 1, 0.32, 1) backwards;
}

.management-card:nth-child(1) { animation-delay: 0.1s; }
.management-card:nth-child(2) { animation-delay: 0.2s; }
.management-card:nth-child(3) { animation-delay: 0.3s; }
.management-card:nth-child(4) { animation-delay: 0.4s; }
.management-card:nth-child(5) { animation-delay: 0.5s; }
.management-card:nth-child(6) { animation-delay: 0.6s; }

@keyframes cardEntrance {
  from {
    opacity: 0;
    transform: translateY(40px) rotateX(-10deg) scale(0.9);
    filter: blur(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0) rotateX(0deg) scale(1);
    filter: blur(0);
  }
}

/* 管理卡片 - 高級3D黑金奢華視覺效果 */
.management-card {
  background: 
    linear-gradient(145deg, var(--bg-secondary) 0%, var(--bg-primary) 100%),
    repeating-linear-gradient(
      45deg,
      transparent,
      transparent 35px,
      rgba(16, 185, 129, 0.01) 35px,
      rgba(16, 185, 129, 0.01) 70px
    );
  border: 1px solid rgba(16, 185, 129, 0.15);
  border-radius: 24px;
  padding: 0;
  position: relative;
  transition: all var(--transition-base) var(--ease-out);
  overflow: hidden;
  transform-style: preserve-3d;
  perspective: 1000px;
  
  /* 豪華3D多層陰影 */
  box-shadow: 
    0 4px 8px rgba(0, 0, 0, 0.4),
    0 8px 16px rgba(0, 0, 0, 0.3),
    0 16px 32px rgba(0, 0, 0, 0.2),
    0 32px 64px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(16, 185, 129, 0.15),
    inset 0 -1px 0 rgba(0, 0, 0, 0.4);
    
  /* 多層內部光暈效果 */
  background-image: 
    linear-gradient(145deg, var(--bg-secondary) 0%, var(--bg-primary) 100%),
    radial-gradient(ellipse at 30% 0%, rgba(16, 185, 129, 0.1) 0%, transparent 40%),
    radial-gradient(ellipse at 70% 100%, rgba(16, 185, 129, 0.05) 0%, transparent 50%);
}

/* 卡片頂部光暈裝飾 */
.management-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--primary-gold), transparent);
  opacity: 0.3;
  animation: shimmer 3s ease-in-out infinite;
}

/* 卡片懸浮效果 - 3D奢華動態 */
.management-card {
  transform: rotateX(0deg) rotateY(0deg);
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
}

.management-card:hover {
  transform: translateY(-8px) rotateX(2deg) rotateY(-2deg) scale(1.02);
  border-color: rgba(16, 185, 129, 0.4);
  box-shadow: 
    0 15px 30px rgba(0, 0, 0, 0.5),
    0 25px 50px rgba(0, 0, 0, 0.4),
    0 35px 70px rgba(0, 0, 0, 0.3),
    0 0 60px rgba(16, 185, 129, 0.2),
    inset 0 1px 0 rgba(16, 185, 129, 0.3),
    inset 0 -1px 0 rgba(0, 0, 0, 0.5);
}

/* 磁性吸附效果 */
.management-card::after {
  content: '';
  position: absolute;
  inset: -2px;
  background: linear-gradient(45deg, var(--primary-gold), transparent, var(--primary-gold));
  border-radius: 24px;
  opacity: 0;
  z-index: -1;
  transition: opacity 0.3s ease;
  filter: blur(10px);
}

.management-card:hover::after {
  opacity: 0.2;
  animation: magneticGlow 2s ease-in-out infinite;
}

.management-card:hover::before {
  opacity: 0.6;
}

/* 卡片頭部 - 金色底線 */
.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 28px 32px 24px;
  border-bottom: 1px solid rgba(16, 185, 129, 0.1);
  background: linear-gradient(180deg, rgba(16, 185, 129, 0.03) 0%, transparent 100%);
  position: relative;
}

/* 卡片頭部裝飾線 */
.card-header::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.3), transparent);
  animation: shimmer 4s ease-in-out infinite;
}

.card-icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-gold);
  background: 
    radial-gradient(circle at 30% 30%, rgba(255, 251, 230, 0.1) 0%, transparent 50%),
    radial-gradient(circle at center, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%);
  border-radius: 16px;
  border: 1px solid rgba(16, 185, 129, 0.25);
  flex-shrink: 0;
  position: relative;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  box-shadow: 
    0 4px 12px rgba(16, 185, 129, 0.3),
    inset 0 2px 4px rgba(255, 251, 230, 0.1),
    inset 0 -2px 4px rgba(191, 166, 88, 0.2);
  transform-style: preserve-3d;
  backdrop-filter: blur(10px);
}

/* 圖標內部發光 */
.card-icon::before {
  content: '';
  position: absolute;
  inset: 4px;
  background: radial-gradient(circle, rgba(16, 185, 129, 0.2) 0%, transparent 70%);
  border-radius: 12px;
  filter: blur(8px);
  opacity: 0;
  transition: opacity 0.3s ease;
}

/* 圖標3D發光效果 - 高級動態 */
.management-card:hover .card-icon {
  background: 
    radial-gradient(circle at 30% 30%, rgba(255, 251, 230, 0.2) 0%, transparent 50%),
    radial-gradient(circle at center, rgba(16, 185, 129, 0.25) 0%, rgba(16, 185, 129, 0.1) 100%);
  border-color: var(--primary-gold);
  transform: translateZ(10px) scale(1.1) rotate(10deg);
  box-shadow: 
    0 8px 24px rgba(16, 185, 129, 0.5),
    0 0 40px rgba(16, 185, 129, 0.4),
    inset 0 2px 4px rgba(255, 251, 230, 0.3),
    inset 0 -2px 4px rgba(191, 166, 88, 0.3);
  animation: iconFloat 3s ease-in-out infinite;
}

.management-card:hover .card-icon::before {
  opacity: 1;
  animation: innerGlow 2s ease-in-out infinite;
}

.management-card:hover .card-icon svg {
  filter: drop-shadow(0 0 8px var(--primary-gold));
  animation: iconRotate 8s linear infinite;
}

/* 標題組 - 主標題和副標題 */
.title-group {
  margin-bottom: 20px;
  animation: fadeInUp 0.4s var(--ease-out);
}

.card-title {
  font-size: 22px;
  font-weight: 700;
  background: linear-gradient(90deg, var(--text-primary) 0%, var(--primary-gold) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0 0 6px 0;
  letter-spacing: 0.8px;
  text-transform: capitalize;
  display: flex;
  align-items: center;
  gap: 12px;
}

/* 卡片副標題 - 功能描述 */
.card-subtitle {
  font-size: 14px;
  font-weight: 400;
  color: var(--text-muted);
  line-height: 1.4;
  margin: 0 0 0 40px; /* 對齊圖標寬度 */
  opacity: 0.8;
  transition: opacity var(--transition-fast) var(--ease-out);
}

/* 懸浮時增強副標題可見度 */
.management-card:hover .card-subtitle {
  opacity: 1;
  color: var(--text-secondary);
}

/* 標題組動畫 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 卡片內容 */
.card-content {
  padding: 28px;
  position: relative;
  animation: fadeIn 0.6s var(--ease-out) 0.2s both;
}

/* 信息顯示 - 高級金色玻璃框架 */
.info-display {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px 32px;
  background: 
    linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(16, 185, 129, 0.04) 100%),
    linear-gradient(90deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.1) 100%);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 20px;
  margin-bottom: 32px;
  position: relative;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  box-shadow: 
    0 6px 16px rgba(0, 0, 0, 0.3),
    inset 0 2px 4px rgba(16, 185, 129, 0.1),
    inset 0 -1px 2px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  backdrop-filter: blur(20px) saturate(150%);
  transform: translateZ(0);
}

/* 信息框懸浮光線 */
.info-display::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent 30%, rgba(16, 185, 129, 0.1) 50%, transparent 70%);
  transform: rotate(45deg);
  transition: all 0.6s ease;
  opacity: 0;
}

/* 信息顯示裝飾 */
.info-display::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, var(--primary-gold) 0%, var(--primary-gold-dark) 100%);
  border-radius: 12px 0 0 12px;
}

.info-display:hover {
  transform: translateY(-2px) translateZ(10px);
  background: 
    linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.06) 100%),
    linear-gradient(90deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.2) 100%);
  border-color: rgba(16, 185, 129, 0.3);
  box-shadow: 
    0 8px 24px rgba(0, 0, 0, 0.4),
    0 0 40px rgba(16, 185, 129, 0.15),
    inset 0 2px 4px rgba(16, 185, 129, 0.15),
    inset 0 -1px 2px rgba(0, 0, 0, 0.3);
}

.info-display:hover::after {
  opacity: 1;
  animation: infoShine 2s ease-in-out infinite;
}

.info-label {
  color: var(--text-muted);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  opacity: 0.8;
}

.info-value {
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 700;
  transition: all var(--transition-fast) var(--ease-out);
}

.info-value.highlight {
  font-size: 28px;
  font-weight: 800;
  background: linear-gradient(90deg, var(--primary-gold-light) 0%, var(--primary-gold) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 30px rgba(16, 185, 129, 0.4);
  animation: goldShine 3s ease infinite;
}

/* 地址顯示 - 高級黑金對比 */
.address-display {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 28px 32px;
  background: 
    linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.4) 100%),
    repeating-linear-gradient(
      90deg,
      transparent,
      transparent 50px,
      rgba(16, 185, 129, 0.02) 50px,
      rgba(16, 185, 129, 0.02) 100px
    );
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 20px;
  margin-bottom: 32px;
  position: relative;
  overflow: hidden;
  box-shadow: 
    inset 0 4px 8px rgba(0, 0, 0, 0.5),
    inset 0 -2px 4px rgba(0, 0, 0, 0.3),
    0 2px 4px rgba(16, 185, 129, 0.1);
  backdrop-filter: blur(10px);
}

/* 地址顯示背景裝飾 */
.address-display::after {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(16, 185, 129, 0.03) 0%, transparent 70%);
  animation: rotate 20s linear infinite;
  pointer-events: none;
}

.address-label {
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  z-index: 1;
}

.address-value {
  color: var(--text-primary);
  font-size: 14px;
  font-family: 'SF Mono', 'Monaco', 'Courier New', monospace;
  font-weight: 500;
  word-break: break-all;
  background: 
    linear-gradient(90deg, rgba(16, 185, 129, 0.08) 0%, rgba(16, 185, 129, 0.03) 100%),
    rgba(0, 0, 0, 0.3);
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid rgba(16, 185, 129, 0.15);
  z-index: 1;
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  box-shadow: 
    inset 0 2px 4px rgba(0, 0, 0, 0.2),
    0 1px 2px rgba(16, 185, 129, 0.1);
  letter-spacing: 0.5px;
}

.address-value:hover {
  background: rgba(16, 185, 129, 0.08);
  border-color: rgba(16, 185, 129, 0.2);
}

.address-value.small {
  font-size: 12px;
  padding: 6px 10px;
}

/* 表單元素 - 增強視覺效果 */
.form-group {
  margin-bottom: 24px;
  position: relative;
  animation: fadeIn 0.6s var(--ease-out) backwards;
}

.form-group:nth-child(1) { animation-delay: 0.1s; }
.form-group:nth-child(2) { animation-delay: 0.2s; }
.form-group:nth-child(3) { animation-delay: 0.3s; }

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  letter-spacing: 0.5px;
  transition: color var(--transition-fast) var(--ease-out);
}

/* 表單焦點狀態 */
.form-group:focus-within .form-label {
  color: var(--primary-gold);
}

/* 表單提示 */
.form-hint {
  font-size: 12px;
  color: var(--text-muted);
  margin-top: 6px;
  opacity: 0.8;
}

.form-input {
  width: 100%;
  padding: 20px 28px;
  background: 
    linear-gradient(145deg, rgba(0, 0, 0, 0.6) 0%, rgba(24, 18, 11, 0.4) 100%),
    linear-gradient(180deg, var(--bg-primary) 0%, rgba(42, 32, 21, 0.8) 100%);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 16px;
  color: var(--text-primary);
  font-size: 16px;
  font-family: inherit;
  font-weight: 500;
  letter-spacing: 0.5px;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  outline: none;
  position: relative;
  backdrop-filter: blur(10px);
  
  /* 高級內凹效果 */
  box-shadow: 
    inset 0 4px 8px rgba(0, 0, 0, 0.5),
    inset 0 -2px 4px rgba(0, 0, 0, 0.3),
    inset 0 1px 1px rgba(16, 185, 129, 0.05),
    0 1px 0 rgba(16, 185, 129, 0.15),
    0 2px 4px rgba(0, 0, 0, 0.3);
}

/* 輸入框光標動畫 */
.form-input::selection {
  background: rgba(16, 185, 129, 0.3);
  color: var(--text-primary);
}

.form-input::placeholder {
  color: var(--text-muted);
  opacity: 0.6;
}

.form-input:hover:not(:disabled) {
  border-color: rgba(16, 185, 129, 0.3);
  background: rgba(26, 32, 21, 0.8);
}

.form-input:focus {
  border-color: var(--primary-gold);
  background: 
    linear-gradient(145deg, rgba(0, 0, 0, 0.7) 0%, rgba(24, 18, 11, 0.5) 100%),
    linear-gradient(180deg, var(--bg-primary) 0%, rgba(42, 32, 21, 0.9) 100%);
  transform: translateY(-2px);
  box-shadow: 
    0 0 0 3px rgba(16, 185, 129, 0.15),
    0 0 30px rgba(16, 185, 129, 0.2),
    0 8px 16px rgba(0, 0, 0, 0.4),
    inset 0 4px 8px rgba(0, 0, 0, 0.5),
    inset 0 1px 1px rgba(16, 185, 129, 0.1);
}

/* 輸入框浮動標籤效果 */
.form-group {
  position: relative;
}

.form-label {
  transition: all 0.3s ease;
  transform-origin: left center;
}

.form-group:focus-within .form-label {
  transform: translateY(-2px) scale(0.95);
  color: var(--primary-gold);
  text-shadow: 0 0 20px rgba(16, 185, 129, 0.5);
}

.form-input:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: rgba(24, 18, 11, 0.5);
}

.address-input {
  font-family: monospace;
  font-size: 14px;
}

/* 輸入組 */
.input-group {
  display: flex;
  gap: 12px;
  align-items: stretch;
}

.input-group .form-input {
  flex: 1;
}

/* 表單網格 - 優化間距 */
.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
  margin-bottom: 28px;
  padding: 24px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  border: 1px solid rgba(16, 185, 129, 0.08);
}

/* 按鈕 */
.btn {
  padding: 18px 36px;
  background: 
    linear-gradient(135deg, var(--primary-gold-light) 0%, var(--primary-gold) 50%, var(--primary-gold-dark) 100%),
    linear-gradient(135deg, var(--primary-gold) 0%, var(--primary-gold-light) 100%);
  background-size: 200% 100%, 100% 100%;
  background-position: 0% 0%, 0% 0%;
  background-blend-mode: normal, multiply;
  color: var(--text-inverse);
  font-size: 14px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  border: 1px solid rgba(255, 251, 230, 0.2);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 52px;
  white-space: nowrap;
  transform-style: preserve-3d;
  
  /* 高級3D按鈕效果 */
  box-shadow: 
    0 6px 12px rgba(0, 0, 0, 0.4),
    0 12px 24px rgba(16, 185, 129, 0.3),
    inset 0 2px 4px rgba(255, 255, 255, 0.4),
    inset 0 -2px 4px rgba(191, 166, 88, 0.4),
    0 0 0 1px rgba(16, 185, 129, 0.2);
}

/* 按鈕光澤動畫 - 金色光暈效果 */
.btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 251, 230, 0.4), transparent);
  transition: left 0.6s var(--ease-out);
  filter: blur(5px);
}

/* 按鈕內部發光 */
.btn::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 8px;
  background: radial-gradient(circle at center, rgba(16, 185, 129, 0.2) 0%, transparent 70%);
  opacity: 0;
  transition: opacity var(--transition-base) var(--ease-out);
}

/* 按鄕3D懸浮效果 - 高級黃金光澤 */
.btn:hover {
  transform: translateY(-4px) translateZ(10px) scale(1.03);
  background-position: 100% 0%, 0% 0%;
  border-color: var(--primary-gold);
  box-shadow: 
    0 10px 20px rgba(0, 0, 0, 0.5),
    0 20px 40px rgba(16, 185, 129, 0.4),
    inset 0 2px 4px rgba(255, 255, 255, 0.5),
    inset 0 -2px 4px rgba(191, 166, 88, 0.5),
    0 0 50px rgba(16, 185, 129, 0.6),
    0 0 0 2px rgba(16, 185, 129, 0.3);
}

.btn:hover::before {
  animation: buttonWave 1.5s ease-in-out infinite;
}

/* 按鈕按下效果 */
.btn:active {
  transform: translateY(-2px) translateZ(5px) scale(1.01);
  box-shadow: 
    0 4px 8px rgba(0, 0, 0, 0.4),
    0 8px 16px rgba(16, 185, 129, 0.3),
    inset 0 2px 4px rgba(0, 0, 0, 0.2),
    inset 0 -1px 2px rgba(191, 166, 88, 0.4),
    0 0 30px rgba(16, 185, 129, 0.4);
}

.btn:hover::before {
  left: 100%;
}

.btn:hover::after {
  opacity: 1;
}

/* 按鈕按下效果 */
.btn:active {
  transform: translateY(0);
  box-shadow: 
    0 1px 2px rgba(0, 0, 0, 0.2),
    inset 0 1px 2px rgba(0, 0, 0, 0.2);
}

/* 按鈕禁用狀態 */
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

/* 次要按鈕 */
.btn-secondary {
  background: transparent;
  color: var(--primary-gold);
  border: 1px solid var(--primary-gold);
  box-shadow: none;
}

.btn-secondary::before {
  background: linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.3), transparent);
}

.btn-secondary:hover {
  background: rgba(16, 185, 129, 0.1);
  border-color: var(--primary-gold-light);
  box-shadow: 
    0 0 16px rgba(16, 185, 129, 0.2),
    inset 0 0 20px rgba(16, 185, 129, 0.1);
}

/* 全寬按鈕 */
.btn.full-width {
  width: 100%;
}

/* 按鄕3D加載動畫 - 高級金色效果 */
.btn-loading {
  display: inline-block;
  width: 16px;
  height: 16px;
  position: relative;
}

.btn-loading::before,
.btn-loading::after {
  content: '';
  position: absolute;
  inset: 0;
  border: 2px solid transparent;
  border-radius: 50%;
  animation: loadingRotate 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.btn-loading::before {
  border-top-color: var(--text-inverse);
  animation-delay: -0.45s;
}

.btn-loading::after {
  border-top-color: rgba(24, 18, 11, 0.5);
  border-right-color: rgba(24, 18, 11, 0.5);
  animation-delay: -0.15s;
}

@keyframes loadingRotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 加載狀態遮罩 - 高級黑金玻璃效果 */
.loading-overlay {
  position: absolute;
  inset: 0;
  background: 
    radial-gradient(circle at center, rgba(16, 185, 129, 0.1) 0%, transparent 50%),
    linear-gradient(135deg, rgba(24, 18, 11, 0.95) 0%, rgba(42, 32, 21, 0.9) 100%);
  backdrop-filter: blur(12px) saturate(180%) brightness(0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 24px;
  opacity: 0;
  pointer-events: none;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
}

.loading-overlay.active {
  opacity: 1;
  pointer-events: all;
  backdrop-filter: blur(16px) saturate(200%) brightness(0.7);
}

.loading-overlay.active {
  opacity: 1;
  pointer-events: all;
}

/* 3D加載旋轉器 */
.loading-spinner {
  width: 60px;
  height: 60px;
  position: relative;
  transform-style: preserve-3d;
  animation: spinnerRotate 2s linear infinite;
}

.loading-spinner::before,
.loading-spinner::after {
  content: '';
  position: absolute;
  inset: 0;
  border: 3px solid transparent;
  border-radius: 50%;
}

.loading-spinner::before {
  border-top-color: var(--primary-gold);
  border-bottom-color: var(--primary-gold);
  animation: spinnerPulse 1.5s ease-in-out infinite;
}

.loading-spinner::after {
  border-left-color: rgba(16, 185, 129, 0.5);
  border-right-color: rgba(16, 185, 129, 0.5);
  animation: spinnerPulse 1.5s ease-in-out infinite 0.75s;
  transform: rotateY(90deg);
}

@keyframes spinnerRotate {
  0% { transform: rotateX(35deg) rotateY(0deg); }
  100% { transform: rotateX(35deg) rotateY(360deg); }
}

@keyframes spinnerPulse {
  0%, 100% { 
    transform: scale(1);
    filter: drop-shadow(0 0 10px var(--primary-gold));
  }
  50% { 
    transform: scale(1.1);
    filter: drop-shadow(0 0 20px var(--primary-gold));
  }
}

/* 按鈕組 - 高級交互體驗 */
.button-group {
  display: flex;
  gap: 16px;
  margin-top: 28px;
  position: relative;
  padding: 4px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 20px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.3);
}

.button-group .btn {
  flex: 1;
  transition: all var(--transition-base) var(--ease-out);
}

/* 按鈕組懸浮效果 */
.button-group:hover .btn {
  opacity: 0.8;
}

.button-group:hover .btn:hover {
  opacity: 1;
  transform: translateY(-2px) scale(1.05);
}

/* Boost 等級網格 - 高級金色立體網格 */
.boost-levels-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 20px;
  margin-bottom: 28px;
  padding: 32px;
  background: 
    linear-gradient(135deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.3) 100%),
    radial-gradient(ellipse at top right, rgba(16, 185, 129, 0.08) 0%, transparent 50%),
    repeating-linear-gradient(
      45deg,
      transparent,
      transparent 20px,
      rgba(16, 185, 129, 0.02) 20px,
      rgba(16, 185, 129, 0.02) 40px
    );
  border-radius: 20px;
  border: 1px solid rgba(16, 185, 129, 0.2);
  box-shadow: 
    inset 0 4px 8px rgba(0, 0, 0, 0.4),
    inset 0 -2px 4px rgba(0, 0, 0, 0.2),
    0 2px 4px rgba(16, 185, 129, 0.1);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  transform-style: preserve-3d;
  perspective: 1000px;
}

.boost-level-item {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(16, 185, 129, 0.03) 100%);
  border: 1px solid rgba(16, 185, 129, 0.1);
  border-radius: 10px;
  transition: all var(--transition-fast) var(--ease-out);
  position: relative;
}

/* Boost 等級懸浮光暈 */
.boost-level-item::before {
  content: '';
  position: absolute;
  inset: -1px;
  background: linear-gradient(135deg, var(--primary-gold), transparent);
  border-radius: 10px;
  opacity: 0;
  transition: opacity var(--transition-fast) var(--ease-out);
  z-index: -1;
}

.boost-level-item:hover {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(16, 185, 129, 0.06) 100%);
  transform: translateY(-4px) scale(1.02);
  border-color: rgba(16, 185, 129, 0.3);
  box-shadow: 
    0 4px 12px rgba(16, 185, 129, 0.2),
    0 8px 24px rgba(0, 0, 0, 0.2);
}

.boost-level-item:hover::before {
  opacity: 0.15;
}

/* 授權列表 */
.authorized-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.authorized-item {
  background: rgba(16, 185, 129, 0.05);
  border: 1px solid rgba(16, 185, 129, 0.1);
  border-radius: 12px;
  padding: 20px;
  transition: all var(--transition-fast) var(--ease-out);
  position: relative;
  overflow: hidden;
}

/* 授權項目裝飾 */
.authorized-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, var(--primary-gold) 0%, var(--primary-gold-dark) 100%);
  transform: scaleY(0);
  transition: transform var(--transition-base) var(--ease-out);
  box-shadow: 0 0 10px rgba(16, 185, 129, 0.5);
}

.authorized-item:hover {
  background: rgba(16, 185, 129, 0.08);
  border-color: rgba(16, 185, 129, 0.2);
  transform: translateX(4px);
}

.authorized-item:hover::before {
  transform: scaleY(1);
}

.authorized-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.info-row .info-label {
  min-width: 60px;
  flex-shrink: 0;
}

/* 空狀態 - 高級奢華留白設計 */
.empty-state {
  text-align: center;
  padding: 100px 60px;
  color: var(--text-muted);
  background: 
    radial-gradient(circle at center, rgba(16, 185, 129, 0.1) 0%, transparent 60%),
    repeating-radial-gradient(
      circle at center,
      transparent,
      transparent 40px,
      rgba(16, 185, 129, 0.02) 40px,
      rgba(16, 185, 129, 0.02) 80px
    ),
    linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
  border-radius: 24px;
  border: 2px dashed rgba(16, 185, 129, 0.4);
  box-shadow: 
    inset 0 4px 12px rgba(0, 0, 0, 0.4),
    inset 0 -2px 8px rgba(0, 0, 0, 0.2),
    0 8px 32px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
}

.empty-state:hover {
  transform: scale(1.02);
  border-color: rgba(16, 185, 129, 0.6);
  box-shadow: 
    inset 0 4px 16px rgba(0, 0, 0, 0.5),
    inset 0 -2px 12px rgba(0, 0, 0, 0.3),
    0 12px 40px rgba(0, 0, 0, 0.4),
    0 0 60px rgba(16, 185, 129, 0.1);
}

.empty-state p {
  margin: 0;
  font-size: 16px;
  opacity: 0.8;
}

/* 空狀態圖標 */
.empty-state::before {
  content: '\2022\2022\2022';
  display: block;
  font-size: 32px;
  color: var(--primary-gold);
  opacity: 0.3;
  margin-bottom: 16px;
  letter-spacing: 8px;
  animation: float 3s ease-in-out infinite;
}

/* 稅率管理卡片特殊樣式 */
.tax-rate-card {
  position: relative;
  overflow: visible;
}

/* 當前稅率顯示區 */
.tax-rate-display {
  padding: 28px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%);
  border: 1px solid rgba(16, 185, 129, 0.25);
  border-radius: 20px;
  margin-bottom: 32px;
  position: relative;
  box-shadow: 
    0 4px 12px rgba(0, 0, 0, 0.3),
    inset 0 2px 4px rgba(16, 185, 129, 0.1);
}

.current-rate-header {
  border-bottom: 1px solid rgba(16, 185, 129, 0.1);
  padding-bottom: 10px;
  margin-bottom: 15px;
}

.current-rate-info {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  margin-bottom: 20px;
}

.current-rate-box {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
  background: rgba(16, 185, 129, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(16, 185, 129, 0.15);
  transition: all 0.3s ease;
  min-width: 0;
}

.current-rate-box:hover {
  background: rgba(16, 185, 129, 0.08);
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(16, 185, 129, 0.2);
}

.rate-label {
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
}

.rate-value {
  font-size: 24px;
  font-weight: 800;
  color: var(--primary-gold);
  /* Fallback color in case gradient doesn't work */
  background: linear-gradient(135deg, var(--primary-gold-light) 0%, var(--primary-gold) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  /* Only apply transparent fill if background-clip is supported */
  text-shadow: 0 0 30px rgba(16, 185, 129, 0.5);
}

/* Fallback for browsers that don't support background-clip: text */
@supports not (background-clip: text) {
  .rate-value {
    color: var(--primary-gold) !important;
    -webkit-text-fill-color: var(--primary-gold) !important;
  }
}

@keyframes goldPulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.95; transform: scale(1.05); }
}

.rate-indicator {
  height: 6px;
  background: linear-gradient(90deg, var(--primary-gold) 0%, var(--primary-gold-light) 100%);
  border-radius: 3px;
  box-shadow: 0 0 10px rgba(16, 185, 129, 0.5);
  transition: width 0.5s cubic-bezier(0.23, 1, 0.32, 1);
  position: relative;
}

.rate-indicator::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
  height: 12px;
  background: var(--primary-gold);
  border: 2px solid var(--primary-gold-light);
  border-radius: 50%;
  box-shadow: 0 0 15px rgba(16, 185, 129, 0.8);
}

/* 稅率配置表單 */
.tax-form-section {
  padding: 32px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 20px;
  margin-bottom: 32px;
  border: 1px solid rgba(16, 185, 129, 0.1);
}

.form-row {
  margin-bottom: 32px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.field-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.label-text {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.label-hint {
  color: var(--text-muted);
  font-size: 12px;
  padding: 4px 8px;
  background: rgba(16, 185, 129, 0.1);
  border-radius: 6px;
}

.label-value {
  color: var(--primary-gold);
  font-size: 14px;
  font-weight: 700;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.tax-input {
  width: 100%;
  padding: 16px 48px 16px 20px;
  background: rgba(0, 0, 0, 0.5);
  border: 2px solid rgba(16, 185, 129, 0.2);
  border-radius: 12px;
  color: var(--text-primary);
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  outline: none;
}

.tax-input:focus {
  border-color: var(--primary-gold);
  background: rgba(0, 0, 0, 0.7);
  box-shadow: 
    0 0 0 4px rgba(16, 185, 129, 0.1),
    0 0 30px rgba(16, 185, 129, 0.3);
}

.tax-input.has-value {
  color: var(--primary-gold);
}

.input-suffix {
  position: absolute;
  right: 20px;
  color: var(--text-muted);
  font-size: 18px;
  font-weight: 600;
}

/* 分配區域 */
.distribution-section {
  margin-top: 32px;
  padding-top: 32px;
  border-top: 1px solid rgba(16, 185, 129, 0.1);
}

.section-title {
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 24px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.distribution-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
}

.distribution-field {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* 滑塊樣式 */
.slider-wrapper {
  position: relative;
  height: 40px;
  display: flex;
  align-items: center;
}

.tax-slider {
  width: 100%;
  height: 8px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
}

.tax-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 24px;
  height: 24px;
  background: var(--primary-gold);
  border: 3px solid var(--primary-gold-light);
  border-radius: 50%;
  cursor: pointer;
  position: relative;
  z-index: 2;
  box-shadow: 0 0 10px rgba(16, 185, 129, 0.5);
  transition: all 0.2s ease;
}

.tax-slider::-webkit-slider-thumb:hover {
  transform: scale(1.2);
  box-shadow: 0 0 20px rgba(16, 185, 129, 0.8);
}

.slider-fill {
  position: absolute;
  left: 0;
  height: 8px;
  background: linear-gradient(90deg, var(--primary-gold) 0%, var(--primary-gold-light) 100%);
  border-radius: 4px;
  pointer-events: none;
  transition: width 0.2s ease;
  box-shadow: 0 0 10px rgba(16, 185, 129, 0.3);
}

.slider-fill.burn-fill {
  background: linear-gradient(90deg, #ff6b6b 0%, #ff8e53 100%);
  box-shadow: 0 0 10px rgba(255, 107, 107, 0.3);
}

/* 分配預覽 */
.distribution-preview {
  padding: 20px;
  background: rgba(16, 185, 129, 0.05);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 12px;
  margin-top: 24px;
  animation: fadeIn 0.3s ease;
}

.preview-title {
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 16px;
}

.preview-bars {
  display: flex;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.3);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.4);
}

.preview-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-inverse);
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  position: relative;
  overflow: hidden;
}

.preview-bar span {
  position: relative;
  z-index: 1;
  white-space: nowrap;
}

.preview-bar.liquidity {
  background: linear-gradient(135deg, #6ee7b7 0%, #44a39a 100%);
}

.preview-bar.burn {
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%);
}

.preview-bar.team {
  background: linear-gradient(135deg, var(--primary-gold) 0%, var(--primary-gold-dark) 100%);
}

.preview-bar::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  animation: shimmer 2s ease-in-out infinite;
}

/* 驗證訊息 */
.validation-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 8px;
  color: #ff6b6b;
  font-size: 14px;
  margin-top: 16px;
  animation: shake 0.5s ease;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
  20%, 40%, 60%, 80% { transform: translateX(2px); }
}

.validation-message svg {
  flex-shrink: 0;
}

/* 更新按鈕區域 */
.update-button-section {
  text-align: center;
  padding: 32px 0 0;
  border-top: 1px solid rgba(16, 185, 129, 0.1);
}

.btn-update-tax {
  min-width: 280px;
  padding: 20px 40px;
  font-size: 16px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2px;
  background: linear-gradient(135deg, var(--primary-gold) 0%, var(--primary-gold-dark) 100%);
  border: 2px solid var(--primary-gold-light);
  border-radius: 16px;
  color: var(--text-inverse);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-shadow: 
    0 8px 16px rgba(0, 0, 0, 0.4),
    0 0 40px rgba(16, 185, 129, 0.3),
    inset 0 2px 4px rgba(255, 255, 255, 0.3);
}

.btn-update-tax::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  transform: translateX(-100%);
  transition: transform 0.6s ease;
}

.btn-update-tax:not(:disabled):hover {
  transform: translateY(-4px) scale(1.05);
  box-shadow: 
    0 12px 24px rgba(0, 0, 0, 0.5),
    0 0 60px rgba(16, 185, 129, 0.5),
    inset 0 2px 4px rgba(255, 255, 255, 0.4);
}

.btn-update-tax:not(:disabled):hover::before {
  transform: translateX(100%);
}

.btn-update-tax:active {
  transform: translateY(-2px) scale(1.02);
}

.btn-update-tax:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

/* 準備就緒脈衝效果 */
.btn-update-tax.ready-pulse {
  animation: readyPulse 1.5s ease-in-out infinite;
}

@keyframes readyPulse {
  0%, 100% {
    box-shadow: 
      0 8px 16px rgba(0, 0, 0, 0.4),
      0 0 40px rgba(16, 185, 129, 0.3),
      inset 0 2px 4px rgba(255, 255, 255, 0.3);
  }
  50% {
    box-shadow: 
      0 8px 16px rgba(0, 0, 0, 0.4),
      0 0 80px rgba(16, 185, 129, 0.6),
      inset 0 2px 4px rgba(255, 255, 255, 0.4);
  }
}

.btn-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(24, 18, 11, 0.3);
  border-top-color: var(--text-inverse);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.button-hint {
  margin-top: 12px;
  color: var(--text-muted);
  font-size: 13px;
  opacity: 0.8;
}

/* 工具類 */
.mb-3 {
  margin-bottom: 16px;
}

.mb-4 {
  margin-bottom: 24px;
}

/* 動畫 - 高級黑金主題動畫系統 */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
    filter: blur(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
}

@keyframes titleShine {
  0%, 100% { 
    background-position: 0% 0%, 0% 0%;
    transform: scale(1);
  }
  50% { 
    background-position: 100% 0%, 0% 0%;
    transform: scale(1.02);
  }
}

@keyframes buttonWave {
  0% { left: -100%; }
  100% { left: 200%; }
}

@keyframes infoShine {
  0% { transform: rotate(45deg) translateX(-100%); }
  100% { transform: rotate(45deg) translateX(100%); }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes buttonShine {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

@keyframes iconFloat {
  0%, 100% { 
    transform: translateZ(10px) scale(1.1) rotate(10deg) translateY(0);
  }
  50% { 
    transform: translateZ(15px) scale(1.15) rotate(10deg) translateY(-5px);
  }
}

@keyframes iconRotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes innerGlow {
  0%, 100% { 
    opacity: 0.8;
    filter: blur(8px);
  }
  50% { 
    opacity: 1;
    filter: blur(12px);
  }
}

@keyframes magneticGlow {
  0%, 100% { 
    transform: scale(1);
    opacity: 0.2;
  }
  50% { 
    transform: scale(1.05);
    opacity: 0.3;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes glow {
  0%, 100% {
    box-shadow: 0 0 20px rgba(16, 185, 129, 0.3);
  }
  50% {
    box-shadow: 0 0 30px rgba(16, 185, 129, 0.5);
  }
}

@keyframes goldShine {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* 視差滾動效果 */
@media (min-width: 1024px) {
  .management-card {
    transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  }
  
  .management-card:nth-child(odd) {
    transform: translateX(-2px);
  }
  
  .management-card:nth-child(even) {
    transform: translateX(2px);
  }
  
  .management-card:hover {
    z-index: 2;
  }
}

/* 響應式設計 */
@media (max-width: 1024px) {
  .card-group {
    grid-template-columns: 1fr;
  }
  
  .distribution-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .pct-management-root {
    padding: 24px 16px;
  }
  
  .page-title {
    font-size: 28px;
  }
  
  .management-container {
    gap: 16px;
  }
  
  .card-header {
    padding: 20px;
  }
  
  .card-content {
    padding: 20px;
  }
  
  .input-group {
    flex-direction: column;
  }
  
  .button-group {
    flex-direction: column;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .boost-levels-grid {
    grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  }
}

@media (max-width: 640px) {
  .page-title {
    font-size: 24px;
  }
  
  .card-title {
    font-size: 18px;
  }
  
  .card-subtitle {
    font-size: 12px;
    margin-left: 32px;
  }
  
  .btn {
    font-size: 13px;
    padding: 10px 20px;
  }
}

/* 無障礙支持 */
.management-card:focus-within {
  outline: 2px solid var(--primary-gold);
  outline-offset: 4px;
}

.btn:focus-visible,
.form-input:focus-visible {
  outline: 2px solid var(--primary-gold);
  outline-offset: 2px;
}

/* 減少動畫效果 */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>